import { useCallback, useEffect, useRef, useState } from 'react';
import {
  validateBusinessNumberChecksum,
  normalizeRegistrationNumber,
  getErrorMessage,
  isValidErrorCode,
  ErrorCode,
  type CompanyVerificationStatus,
  type CreateSiteRequestedResponse,
  type OrganizationCategory,
} from '@safetyhub/shared';
import { isAgencyOperatedSite, isCreateSiteRequested, operatorGroupLabel } from '../lib/agencySiteLinkView';
import { isSiteInfoLocked, siteInfoLockedReason } from '../lib/siteInfoManage';
import {
  sitesApi,
  Site,
  CreateSiteDto,
  UpdateSiteDto,
  extractApiError,
  isSiteStatusEditable,
  type SiteEditableStatus,
} from '../lib/organizations';
import { companiesApi, CompanyInfoInput } from '../lib/companies';
import type { Group } from '../lib/groups';
import { useToastStore } from '../stores/toast';
import { useAuthStore } from '../stores/auth';
import { isConstructionSiteIndustry, siteIndustryLabel } from '../lib/siteIndustry';

/**
 * 거부 사유 → 사용자 문구. **서버가 「이 자리 전용」으로 쓴 문구가 우선**이다.
 *
 * 종전에는 `code` 만 받아 `getErrorMessage(code)` 로 기본 문구를 **다시 파생**했다 — 서버가
 * `details.message` 로 실어 보낸 자리별 안내가 그 자리에서 **통째로 버려졌다**(유효한 코드면
 * `fallback` 인자조차 영영 안 쓰인다). 2026-10-28 에 서버가 거부 사유를 자리별로 가르면서
 * (줄기 `error-code-reason-axis` P4) 그 손실이 실제 결함이 됐다.
 *
 * ## ⛔ 왜 top-level `message` 가 아니라 `details.message` 인가
 * 선례(admin `apiErrorMessage`)는 top-level `message` 를 쓰고 `NETWORK_ERROR`·`UNKNOWN_ERROR`
 * 만 거르는데, **그 수용은 「보는 사람이 슈퍼관리자뿐」이라는 근거 위에 있고 web 은 그렇지 않다**
 * (그 함수의 docblock 이 「web 으로 옮기려면 `VALIDATION_INVALID_INPUT` 을 폴백 갈래에 넣을지
 * 그 자리에서 판정하라」고 요구한다 — 이것이 그 판정이다).
 * 서버 필터는 class-validator 오류를 `message = resp.message[0]`(**영문 필드명**) +
 * `code = 'VALIDATION_INVALID_INPUT'` + `details = { errors }` 로 내보낸다. top-level 을 쓰면
 * 그 영문이 고객 화면에 뜬다. **`details.message` 는 사람이 쓴 문구에만 실리므로** 그 갈래를
 * 구조적으로 배제한다 — class-validator 는 `details.errors` 를 쓰고 `details.message` 를 쓰지 않는다.
 */
const resolveErrorMessage = (
  apiError: { code: string; message: string; details?: Record<string, unknown> },
  fallback: string,
): string => {
  const authored = apiError.details?.message;
  if (typeof authored === 'string' && authored.trim()) return authored;
  return isValidErrorCode(apiError.code) ? getErrorMessage(apiError.code) : fallback;
};

export type LookupMatchKind = 'fully' | 'org' | null;

/**
 * dirty 판정용 폼 스냅샷 — **SiteFormBody가 노출하는 편집 가능 입력 전수**와 1:1.
 *
 * 필드를 폼에 추가하면 여기에도 넣을 것 — 누락하면 그 필드만 바꿨을 때 "바꿨는데 저장이
 * 안 눌리는" 결함이 된다(useSiteFormState.test가 키 전수를 지킨다). 리터럴 한 곳에서
 * 만들어 키 순서가 고정된다(useDirtySnapshot의 JSON 직렬화 비교 규약).
 */
export interface SiteFormSnapshot {
  // 사업자 축
  registrationNumber: string;
  noRegistrationNumber: boolean;
  sameAsOrganization: boolean;
  companyName: string;
  ceoName: string;
  openingDate: string;
  // 현장 기본
  name: string;
  address: string;
  addressDetail: string;
  zonecode: string;
  latitude: string;
  longitude: string;
  timezone: string;
  industryId: string;
  hazardCategories: string[];
  status: SiteEditableStatus;
  groupId: string;
  // 건설 정보
  constructionCost: string;
  constructionStart: string;
  constructionEnd: string;
  managementNumber: string;
  licenseNumber: string;
  progressRate: string;
  clientName: string;
  clientContact: string;
}

export interface UseSiteFormStateOptions {
  companyId: string | undefined;
  isEdit: boolean;
  siteId: string | undefined;
  onSaved?: (site: Site) => void;
  /**
   * 생성이 «업체 승인 대기»로 끝났다(P4 — 남의 회사를 수검업체로 지정한 생성이 요청이 됐다).
   * 현장이 아직 없으므로 `onSaved`는 부르지 않는다 — 호출부는 현장 상세로 이동하지 말고 안내로 끝낸다.
   */
  onRequested?: (result: CreateSiteRequestedResponse) => void;
}

export interface UseSiteFormStateResult {
  registrationNumber: string;
  setRegistrationNumber: (v: string) => void;
  companyName: string;
  setCompanyName: (v: string) => void;
  ceoName: string;
  setCeoName: (v: string) => void;
  openingDate: string;
  setOpeningDate: (v: string) => void;
  noRegistrationNumber: boolean;
  setNoRegistrationNumber: (v: boolean) => void;
  /**
   * 「조직과 동일」 — 수검업체를 **이 조직 자신의 회사**(`options.companyId`)로 지정한다.
   *
   * 켜면 사업자 검증을 건너뛰고 저장 시 `companyId = options.companyId`·`companyInfo` 생략으로
   * 보낸다(서버 `assertCompanyAssignable`의 자기 회사 갈래가 받는다). 사업자번호 재사용 경로는
   * 조직 회사를 못 찾는다 — 조직 회사 행은 `createdByCompanyId`가 비어 lookup의 「우리 조직이
   * 등록한 행」 갈래에 안 걸리고, 클레임 전이면 같은 번호의 **새 파편**이 만들어진다.
   * 수정 모드는 불러온 수검업체 id가 조직 회사 id와 같으면 켜진 채 열린다.
   */
  sameAsOrganization: boolean;
  handleToggleSameAsOrganization: (checked: boolean) => void;
  /** 현재 조직 유형 — 「조직과 동일」 안내의 톤을 정한다(기관이면 경고 톤). */
  organizationCategory: OrganizationCategory | null;

  isVerifyingStatus: boolean;
  statusVerified: boolean;
  registrationNumberError: string | null;
  existingCompanyId: string | null;
  lookupMatchKind: LookupMatchKind;

  showInfoForm: boolean;
  setShowInfoForm: (v: boolean) => void;
  isVerifyingInfo: boolean;
  infoVerified: boolean;
  setInfoVerified: (v: boolean) => void;
  infoVerifyError: string | null;
  setInfoVerifyError: (v: string | null) => void;
  /**
   * 마지막 검증 실패가 **국세청 일시 장애**였는가.
   *
   * 진짜 미등록·휴폐업·형식 오류와 달리 사용자가 고칠 수 있는 게 없으므로,
   * 폼은 빨간 에러 대신 "'사업자등록번호 없음'으로 먼저 등록" 안내로 갈아 끼운다.
   */
  ntsTemporaryError: boolean;

  existingStatus: CompanyVerificationStatus | null;

  showMismatchModal: boolean;
  setShowMismatchModal: (v: boolean) => void;

  name: string;
  setName: (v: string) => void;
  address: string;
  addressDetail: string;
  zonecode: string;
  setAddress: (data: {
    address: string;
    addressDetail: string;
    zonecode: string;
  }) => void;
  latitude: string;
  setLatitude: (v: string) => void;
  longitude: string;
  setLongitude: (v: string) => void;
  timezone: string;
  setTimezone: (v: string) => void;
  /**
   * 업종 분류(KSIC) `Industry.id` — 빈 문자열이면 미지정. 폼이 쓰는 유일한 업종 칸이다(2026-10-30 — 8개 하드코딩
   * 목록·「세부 업종」 자유 텍스트를 대체). `option`은 고른 업종의 코드 — 건설 단계 판정(`isConstructionIndustry`)이 읽는다.
   */
  industryId: string;
  setIndustryId: (v: string, option?: { code: string } | null) => void;
  /**
   * 레거시 자유 텍스트 업종(「업종 / 세부」) — **읽기 전용 표시**다. 폼은 그 칸을 쓰지 않고 저장 body에도 싣지 않는다.
   * KSIC를 아직 안 고른 옛 현장을 고칠 때 무엇을 골라야 하나의 단서다. 없으면 null.
   */
  legacyIndustryLabel: string | null;
  hazardCategories: string[];
  toggleHazardCategory: (category: string) => void;
  /** 현장 생애주기 — 이관중은 이관 플로우 전용이라 폼에서 고를 수 없다. */
  status: SiteEditableStatus;
  setStatus: (v: SiteEditableStatus) => void;

  /** 소속 그룹 (생성 기본 = 본점, 수정 = 현장의 현재 그룹). */
  groupId: string;
  setGroupId: (v: string) => void;
  /** 그룹 드롭다운 선택지 (auth store의 회사 그룹 목록). */
  groupOptions: Group[];
  /** 그룹 드롭다운 노출 여부 — Owner이면서 회사 그룹이 2개 이상이고 대행 현장이 아닐 때만. */
  canSelectGroup: boolean;
  /**
   * 대행 현장(호출 조직 ≠ 소유 조직)일 때 우리 배치 그룹 이름(읽기 전용 표시) — 소유 현장·생성 모드면 `null`.
   * 근거는 응답 `operatorGroupId`(`isAgencyOperatedSite`).
   */
  agencyOperatorGroupName: string | null;
  /**
   * 수정 모드에서 불러온 현장의 정보 관리권이 업체에 있어(응답 `canManageSiteInfo === false` — O10) 저장할 수 없을 때 그 사유,
   * 아니면 `null`. 값이 있으면 `canSubmit`이 false다. 목록·상세의 수정 진입은 이미 막혀 있고, 이 칸은 `/sites/:id/edit`를
   * 직접 연 경우의 막다른 길(저장 → 403)을 미리 알린다. 판정은 `lib/siteInfoManage`.
   */
  siteInfoLockedReason: string | null;
  /** 수정 모드에서 불러온 그룹과 다른 그룹을 골랐는가 (제출 전 확인 모달의 트리거). */
  isGroupChanged: boolean;
  showGroupChangeConfirm: boolean;
  /** 그룹 이동 확인 후 저장 재개. */
  handleGroupChangeConfirm: () => Promise<Site | null>;
  handleGroupChangeCancel: () => void;

  constructionCost: string;
  setConstructionCost: (v: string) => void;
  constructionStart: string;
  setConstructionStart: (v: string) => void;
  constructionEnd: string;
  setConstructionEnd: (v: string) => void;
  managementNumber: string;
  setManagementNumber: (v: string) => void;
  licenseNumber: string;
  setLicenseNumber: (v: string) => void;
  progressRate: string;
  setProgressRate: (v: string) => void;
  clientName: string;
  setClientName: (v: string) => void;
  clientContact: string;
  setClientContact: (v: string) => void;
  isConstructionOpen: boolean;
  setIsConstructionOpen: (v: boolean | ((prev: boolean) => boolean)) => void;
  isConstructionIndustry: boolean;

  isLoading: boolean;
  isFetching: boolean;

  companyReady: boolean;
  canSubmit: boolean;
  badgeName: string;

  handleVerifyRegistrationNumber: () => Promise<void>;
  handleVerifyInfo: () => Promise<void>;
  handleFillSiteNameFromCompany: () => void;
  handleToggleNoRegistration: (checked: boolean) => void;
  handleRegistrationNumberChange: (value: string) => void;

  performSave: (skipValidateForMismatch?: boolean) => Promise<Site | null>;
  handleMismatchRetryVerification: () => void;
  handleMismatchSaveWithoutVerification: () => Promise<Site | null>;

  loadSite: () => Promise<void>;
  resetForm: () => void;

  /** dirty 판정용 현재 폼 스냅샷 — `useDirtySnapshot`에 그대로 먹인다. */
  formSnapshot: SiteFormSnapshot;
}

export function useSiteFormState(
  options: UseSiteFormStateOptions,
): UseSiteFormStateResult {
  const { companyId, isEdit, siteId, onSaved, onRequested } = options;
  const { showToast } = useToastStore();

  // 소속 그룹 — 선택지는 auth store의 회사 그룹, 지정·이동 권한은 소유자 전용(서버가 최종 방어선).
  // P6-b: 옛 `role === 'OWNER'` → 소유자 **성질**(`isOwner`). 권한 key가 아니다 —
  // 서버 `sites.service.ts` `assertGroupAssignable`이 `isOwnerMember`로 막으며 그 JSDoc이
  // 「그룹 지정·이동은 소유자 성질 전용(권한 키가 아니라 성질 판정, P4 ② (b))」라 적고 있다.
  // `can('site.manage')`로 열면 드롭다운은 보이는데 저장이 403(GROUP_ACCESS_DENIED)으로 죽는다.
  const currentMembership = useAuthStore((s) => s.currentMembership);
  const groupOptions: Group[] = currentMembership?.company?.groups ?? [];
  // P4 — 수정 모드에서 불러온 현장이 **대행** 현장이면(호출 조직 ≠ 소유 조직 — `isAgencyOperatedSite`) 그룹 select를 띄우지 않는다:
  // `groupId`는 소유 조직의 그룹이고 서버가 소유 조직이 아닌 호출의 `groupId`를 403 `GROUP_ACCESS_DENIED`로 막는다(`sites.service.ts`
  // `update`). 대신 우리 배치 그룹(`operatorGroupId`) 이름을 읽기 전용으로 보인다 — 배치 그룹 변경 API는 P4 범위 밖.
  const [operatorGroupId, setOperatorGroupId] = useState<string | null | undefined>(undefined);
  const [isAgencyOperated, setIsAgencyOperated] = useState(false);
  // O10 — 불러온 현장의 정보 관리권이 업체에 있나(`isSiteInfoLocked`). 생성 모드·로드 전은 false.
  const [siteInfoLocked, setSiteInfoLocked] = useState(false);
  const canSelectGroup =
    currentMembership?.isOwner === true && groupOptions.length >= 2 && !isAgencyOperated;
  const defaultGroupId =
    groupOptions.find((g) => g.isDefault)?.id ?? '';

  const [registrationNumber, setRegistrationNumber] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [ceoName, setCeoName] = useState('');
  const [openingDate, setOpeningDate] = useState('');
  const [noRegistrationNumber, setNoRegistrationNumber] = useState(false);
  const [sameAsOrganization, setSameAsOrganization] = useState(false);
  // 조직 회사의 표시 정보(이름·번호·유형)는 현재 멤버십에서 읽는다 — 그 회사가 이 폼의
  // 조직(`companyId`)과 같을 때만. id는 표시 정보 유무와 무관하게 `companyId`가 정본이다.
  const organizationCompany =
    currentMembership?.company && currentMembership.company.id === companyId
      ? currentMembership.company
      : null;
  const organizationCategory: OrganizationCategory | null =
    organizationCompany?.category ?? null;

  const [isVerifyingStatus, setIsVerifyingStatus] = useState(false);
  const [statusVerified, setStatusVerified] = useState(false);
  const [registrationNumberError, setRegistrationNumberError] = useState<
    string | null
  >(null);
  const [existingCompanyId, setExistingCompanyId] = useState<string | null>(
    null,
  );
  const [lookupMatchKind, setLookupMatchKind] = useState<LookupMatchKind>(null);

  const [showInfoForm, setShowInfoForm] = useState(false);
  const [isVerifyingInfo, setIsVerifyingInfo] = useState(false);
  const [infoVerified, setInfoVerified] = useState(false);
  const [infoVerifyError, setInfoVerifyError] = useState<string | null>(null);
  const [ntsTemporaryError, setNtsTemporaryError] = useState(false);

  const [existingStatus, setExistingStatus] =
    useState<CompanyVerificationStatus | null>(null);

  /**
   * 수정 진입 시 불러온 사업자 축 스냅샷. performSave에서 현재 입력과 대조해
   * **사업자 축이 로드 시점 그대로면 companyInfo·companyId를 모두 생략**한다 —
   * 서버는 제공 안 된 필드를 건드리지 않으므로 기존 링크가 유지되고, NTS resolve를
   * 타지 않아 NTS 장애 중에도 그룹 이동·이름 변경 같은 일반 수정이 막히지 않는다
   * (2026-08-16 실측: 그룹 이동 저장이 무관한 NTS 재검증 503에 막혔다).
   */
  const loadedCompanySnapshotRef = useRef<{
    companyId: string;
    registrationNumber: string;
    companyName: string;
    ceoName: string;
    openingDate: string;
  } | null>(null);

  const [showMismatchModal, setShowMismatchModal] = useState(false);

  const [name, setName] = useState('');
  const [address, setAddressRaw] = useState('');
  const [addressDetail, setAddressDetailRaw] = useState('');
  const [zonecode, setZonecodeRaw] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [timezone, setTimezone] = useState('Asia/Seoul');
  // 레거시 자유 텍스트 업종 — 읽기 전용(편집 진입 때만 채운다 · 저장 body에 싣지 않는다)
  const [legacyIndustryType, setLegacyIndustryType] = useState('');
  const [legacyIndustrySubType, setLegacyIndustrySubType] = useState('');
  const [industryId, setIndustryIdRaw] = useState('');
  // 고른 업종의 KSIC 코드 — 건설 단계 판정 재료(편집 진입 때는 응답의 `site.industry.code`)
  const [industryCode, setIndustryCode] = useState('');
  const setIndustryId = useCallback((id: string, option?: { code: string } | null) => {
    setIndustryIdRaw(id);
    setIndustryCode(id ? (option?.code ?? '') : '');
  }, []);
  // 편집 진입 시 불러온 업종 분류 — 저장 때 «바뀌었을 때만» 싣는다(그대로인 칸을 매번 보내면 서버가 매 저장마다
  // 법정 서류를 다시 실체화한다 — `sites.service` update의 실체화 트리거)
  const initialIndustryIdRef = useRef('');
  const [hazardCategories, setHazardCategories] = useState<string[]>([]);
  const [status, setStatus] = useState<SiteEditableStatus>('ACTIVE');
  // 이관중 현장은 status를 이관 플로우가 소유한다 — 폼이 그 값을 대신 써 보내면 안 되므로
  // 저장 body에서 아예 뺀다(서버 DTO도 ACTIVE/PAUSED/CLOSED만 받는다).
  const [isStatusEditable, setIsStatusEditable] = useState(true);

  const [groupId, setGroupIdRaw] = useState('');
  // 수정 모드에서 불러온 현장의 그룹 — 변경 판정(확인 모달)의 기준점.
  const [initialGroupId, setInitialGroupId] = useState<string | null>(null);
  const [showGroupChangeConfirm, setShowGroupChangeConfirm] = useState(false);
  // 확인 모달을 통과한 뒤의 재저장(사업자 불일치 재시도 포함)에서 같은 확인을 다시 묻지 않는다.
  const groupChangeConfirmedRef = useRef(false);

  const setGroupId = useCallback((v: string) => {
    setGroupIdRaw(v);
    groupChangeConfirmedRef.current = false;
  }, []);

  // 생성 모드 기본값 = 본점(기본 그룹). 수정 모드는 loadSite가 채운다.
  useEffect(() => {
    if (!isEdit && !groupId && defaultGroupId) {
      setGroupIdRaw(defaultGroupId);
    }
  }, [isEdit, groupId, defaultGroupId]);

  const isGroupChanged =
    isEdit &&
    initialGroupId !== null &&
    groupId !== '' &&
    groupId !== initialGroupId;

  const [constructionCost, setConstructionCost] = useState('');
  const [constructionStart, setConstructionStart] = useState('');
  const [constructionEnd, setConstructionEnd] = useState('');
  const [managementNumber, setManagementNumber] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [progressRate, setProgressRate] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientContact, setClientContact] = useState('');
  const [isConstructionOpen, setIsConstructionOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const setAddress = useCallback(
    (data: { address: string; addressDetail: string; zonecode: string }) => {
      setAddressRaw(data.address);
      setAddressDetailRaw(data.addressDetail);
      setZonecodeRaw(data.zonecode);
    },
    [],
  );

  const toggleHazardCategory = useCallback((category: string) => {
    setHazardCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category],
    );
  }, []);

  /**
   * 사업자번호가 바뀌면 **그 번호로 얻은 회사 판정을 전부** 버린다.
   *
   * `existingStatus`(불러온 현장에 붙어 있던 회사의 검증 등급)를 남기면, 새 번호를 검증해
   * 다른 회사가 잡혔을 때 performSave의 "FULLY_VERIFIED 회사는 그대로 유지" 분기가 **옛 등급으로**
   * 통과한다. 그 분기는 `existingCompanyId`(=새 회사 id)를 그대로 `companyId`로 실어 보내므로,
   * 현장 수정에서 현재 값과 다른 회사로 바꿔치는 요청이 나간다(서버가 400으로 거절).
   */
  const resetCompanyVerification = useCallback(() => {
    setStatusVerified(false);
    setExistingCompanyId(null);
    setExistingStatus(null);
    setLookupMatchKind(null);
    setShowInfoForm(false);
    setInfoVerified(false);
    setInfoVerifyError(null);
    setRegistrationNumberError(null);
    setNtsTemporaryError(false);
  }, []);

  const resetForm = useCallback(() => {
    setRegistrationNumber('');
    setCompanyName('');
    setCeoName('');
    setOpeningDate('');
    setNoRegistrationNumber(false);
    setSameAsOrganization(false);
    setIsVerifyingStatus(false);
    setStatusVerified(false);
    setRegistrationNumberError(null);
    setExistingCompanyId(null);
    setLookupMatchKind(null);
    setShowInfoForm(false);
    setIsVerifyingInfo(false);
    setInfoVerified(false);
    setInfoVerifyError(null);
    setNtsTemporaryError(false);
    setExistingStatus(null);
    setShowMismatchModal(false);
    setName('');
    setAddressRaw('');
    setAddressDetailRaw('');
    setZonecodeRaw('');
    setLatitude('');
    setLongitude('');
    setTimezone('Asia/Seoul');
    setLegacyIndustryType('');
    setLegacyIndustrySubType('');
    setIndustryId('');
    initialIndustryIdRef.current = '';
    setHazardCategories([]);
    setStatus('ACTIVE');
    setIsStatusEditable(true);
    setGroupIdRaw('');
    setInitialGroupId(null);
    setOperatorGroupId(undefined);
    setIsAgencyOperated(false);
    // 잠긴 현장을 수정하다 닫고 「현장 추가」를 열면 잠금이 남아 제출이 막혔다(리뷰 1103) —
    // 편집 로드(isSiteInfoLocked(site))의 짝으로 여기서 푼다.
    setSiteInfoLocked(false);
    setShowGroupChangeConfirm(false);
    groupChangeConfirmedRef.current = false;
    setConstructionCost('');
    setConstructionStart('');
    setConstructionEnd('');
    setManagementNumber('');
    setLicenseNumber('');
    setProgressRate('');
    setClientName('');
    setClientContact('');
    setIsConstructionOpen(false);
  }, []);

  const handleVerifyRegistrationNumber = useCallback(async () => {
    const normalized = normalizeRegistrationNumber(registrationNumber);
    if (normalized.length !== 10) {
      setRegistrationNumberError('사업자등록번호 10자리를 입력해주세요.');
      return;
    }
    if (!validateBusinessNumberChecksum(normalized)) {
      setRegistrationNumberError('유효하지 않은 사업자등록번호입니다.');
      return;
    }
    setRegistrationNumberError(null);
    setNtsTemporaryError(false);
    setIsVerifyingStatus(true);
    try {
      const lookup = await companiesApi.lookup(normalized);
      if (lookup.found) {
        const hit = lookup.company;
        setExistingCompanyId(hit.id);
        setCompanyName(hit.name || '');
        setCeoName(hit.ceoName ?? '');
        setOpeningDate(hit.openingDate ?? '');
        setStatusVerified(true);
        const kind: LookupMatchKind =
          hit.verificationStatus === 'FULLY_VERIFIED' ? 'fully' : 'org';
        setLookupMatchKind(kind);
        if (hit.verificationStatus === 'FULLY_VERIFIED') {
          setInfoVerified(true);
        }
        showToast(
          kind === 'fully'
            ? '검증 완료된 사업자입니다. 조회 정보로 계속 진행합니다.'
            : '우리 조직에 등록된 사업자 정보입니다. 조회 정보로 계속 진행합니다.',
          'success',
        );
      } else {
        await companiesApi.verifyStatus(normalized);
        setStatusVerified(true);
        setExistingCompanyId(null);
        setLookupMatchKind(null);
      }
    } catch (err) {
      const apiError = extractApiError(err);
      setNtsTemporaryError(
        apiError.code === ErrorCode.COMPANY_VERIFICATION_TEMPORARY_ERROR,
      );
      setRegistrationNumberError(
        resolveErrorMessage(apiError, apiError.message || '검증에 실패했습니다.'),
      );
    } finally {
      setIsVerifyingStatus(false);
    }
  }, [registrationNumber, showToast]);

  const handleVerifyInfo = useCallback(async () => {
    if (!companyName.trim() || !ceoName.trim() || !openingDate.trim()) {
      showToast('상호, 대표자명, 개업일을 모두 입력해주세요.', 'error');
      return;
    }
    const normalizedReg = normalizeRegistrationNumber(registrationNumber);
    if (normalizedReg.length !== 10) {
      setInfoVerifyError('사업자등록번호 10자리를 입력해주세요.');
      return;
    }
    setIsVerifyingInfo(true);
    setInfoVerifyError(null);
    setNtsTemporaryError(false);
    try {
      const result = await companiesApi.verify({
        registrationNumber: normalizedReg,
        businessName: companyName.trim(),
        ceoName: ceoName.trim(),
        openingDate: openingDate.trim(),
      });
      if (result.canonicalName && result.canonicalName !== companyName.trim()) {
        setCompanyName(result.canonicalName);
      }
      setInfoVerified(true);
      setShowInfoForm(false);
    } catch (err) {
      const apiError = extractApiError(err);
      setNtsTemporaryError(
        apiError.code === ErrorCode.COMPANY_VERIFICATION_TEMPORARY_ERROR,
      );
      setInfoVerifyError(
        resolveErrorMessage(apiError, apiError.message || '사업자 정보 검증에 실패했습니다.'),
      );
    } finally {
      setIsVerifyingInfo(false);
    }
  }, [companyName, ceoName, openingDate, registrationNumber, showToast]);

  const handleFillSiteNameFromCompany = useCallback(() => {
    if (companyName.trim()) setName(companyName.trim());
  }, [companyName]);

  const handleToggleNoRegistration = useCallback(
    (checked: boolean) => {
      setNoRegistrationNumber(checked);
      resetCompanyVerification();
      if (checked) {
        setSameAsOrganization(false);
        setRegistrationNumber('');
      } else {
        setCompanyName('');
        setCeoName('');
        setOpeningDate('');
      }
    },
    [resetCompanyVerification],
  );

  // 켜면 사업자 입력 칸을 조직 회사 정보로 채운다(읽기 전용 표시용 — 저장은 id만 보낸다).
  // 끄면 칸을 비워 기존 검증 흐름으로 돌아간다. 두 방향 모두 이전 번호로 얻은 판정은 버린다.
  const handleToggleSameAsOrganization = useCallback(
    (checked: boolean) => {
      setSameAsOrganization(checked);
      resetCompanyVerification();
      setCeoName('');
      setOpeningDate('');
      if (checked) {
        setNoRegistrationNumber(false);
        setRegistrationNumber(organizationCompany?.registrationNumber ?? '');
        setCompanyName(organizationCompany?.name ?? '');
      } else {
        setRegistrationNumber('');
        setCompanyName('');
      }
    },
    [
      resetCompanyVerification,
      organizationCompany?.registrationNumber,
      organizationCompany?.name,
    ],
  );

  const handleRegistrationNumberChange = useCallback(
    (value: string) => {
      setRegistrationNumber(value);
      resetCompanyVerification();
      setCompanyName('');
      setCeoName('');
      setOpeningDate('');
    },
    [resetCompanyVerification],
  );

  const loadSite = useCallback(async () => {
    if (!companyId || !siteId) return;
    setIsFetching(true);
    try {
      const site = await sitesApi.findById(companyId, siteId);
      setName(site.name);
      setAddressRaw(site.address || '');
      setAddressDetailRaw(site.addressDetail || '');
      setZonecodeRaw(site.zonecode || '');
      setLatitude(site.latitude?.toString() || '');
      setLongitude(site.longitude?.toString() || '');
      setTimezone(site.timezone || 'Asia/Seoul');
      setLegacyIndustryType(site.industryType || '');
      setLegacyIndustrySubType(site.industrySubType || '');
      setIndustryId(site.industryId || '', site.industry);
      initialIndustryIdRef.current = site.industryId || '';
      setHazardCategories(site.hazardCategories || []);
      // 변수에 담으면 type predicate narrowing이 끊긴다 — 술어를 직접 분기에 사용
      if (isSiteStatusEditable(site.status)) {
        setIsStatusEditable(true);
        setStatus(site.status);
      } else {
        setIsStatusEditable(false);
      }
      setGroupIdRaw(site.groupId ?? '');
      setInitialGroupId(site.groupId ?? null);
      setOperatorGroupId(site.operatorGroupId);
      setIsAgencyOperated(isAgencyOperatedSite(site));
      setSiteInfoLocked(isSiteInfoLocked(site));
      groupChangeConfirmedRef.current = false;
      setConstructionCost(site.constructionCost || '');
      setConstructionStart(
        site.constructionStart ? site.constructionStart.slice(0, 10) : '',
      );
      setConstructionEnd(
        site.constructionEnd ? site.constructionEnd.slice(0, 10) : '',
      );
      setManagementNumber(site.managementNumber || '');
      setLicenseNumber(site.licenseNumber || '');
      setProgressRate(
        site.progressRate !== undefined && site.progressRate !== null
          ? String(site.progressRate)
          : '',
      );
      setClientName(site.clientName || '');
      setClientContact(site.clientContact || '');

      if (site.company) {
        const c = site.company;
        // 수검업체가 이 조직 자신의 회사면 「조직과 동일」이 켜진 채 연다(판정 축은 id 하나).
        setSameAsOrganization(c.id === companyId);
        setExistingCompanyId(c.id);
        setCompanyName(c.name ?? '');
        setRegistrationNumber(c.registrationNumber ?? '');
        setCeoName(c.ceoName ?? '');
        setOpeningDate(c.openingDate ?? '');
        loadedCompanySnapshotRef.current = {
          companyId: c.id,
          registrationNumber: normalizeRegistrationNumber(
            c.registrationNumber ?? '',
          ),
          companyName: (c.name ?? '').trim(),
          ceoName: (c.ceoName ?? '').trim(),
          openingDate: (c.openingDate ?? '').trim(),
        };

        const status = (c.verificationStatus ?? null) as
          | CompanyVerificationStatus
          | null;
        setExistingStatus(
          status === 'FULLY_VERIFIED' ||
            status === 'STATUS_VERIFIED' ||
            status === 'NOT_APPLICABLE'
            ? status
            : null,
        );

        if (c.id === companyId) {
          // 「조직과 동일」이 사업자 축을 소유한다 — 등급별 검증 표시는 켜지 않는다.
        } else if (status === 'NOT_APPLICABLE') {
          setNoRegistrationNumber(true);
        } else if (status === 'FULLY_VERIFIED') {
          setStatusVerified(true);
          setInfoVerified(true);
          setLookupMatchKind('fully');
        } else if (status === 'STATUS_VERIFIED') {
          setStatusVerified(true);
          setInfoVerified(false);
          setLookupMatchKind('org');
        }
      }
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    } finally {
      setIsFetching(false);
    }
  }, [companyId, siteId, showToast]);

  useEffect(() => {
    if (isEdit && siteId && companyId) {
      void loadSite();
    }
  }, [isEdit, siteId, companyId, loadSite]);

  const performSave = useCallback(
    async (skipValidateForMismatch = false): Promise<Site | null> => {
      if (!companyId) return null;

      if (!name.trim()) {
        showToast('현장 이름을 입력해주세요.', 'error');
        return null;
      }
      if (name.trim().length < 2) {
        showToast('현장 이름은 2자 이상이어야 합니다.', 'error');
        return null;
      }

      // 그룹 이동은 소속 데이터(보고서·점검·스케줄) 접근 범위가 바뀌는 조작 — 제출 전 확인.
      if (isGroupChanged && canSelectGroup && !groupChangeConfirmedRef.current) {
        setShowGroupChangeConfirm(true);
        return null;
      }

      setIsLoading(true);
      try {
        let resolvedCompanyId: string | null | undefined = undefined;
        let companyInfo: CompanyInfoInput | undefined = undefined;

        const loadedSnapshot = loadedCompanySnapshotRef.current;
        const companyAxisUntouched =
          isEdit &&
          !!existingCompanyId &&
          !!loadedSnapshot &&
          loadedSnapshot.companyId === existingCompanyId &&
          loadedSnapshot.registrationNumber ===
            normalizeRegistrationNumber(registrationNumber) &&
          loadedSnapshot.companyName === companyName.trim() &&
          loadedSnapshot.ceoName === ceoName.trim() &&
          loadedSnapshot.openingDate === openingDate.trim();

        if (sameAsOrganization) {
          // 「조직과 동일」 — 조직 자기 회사 id만 보낸다(companyInfo 생략 = NTS 미호출).
          // 이미 그 회사가 붙어 있던 현장이면 생략해 기존 링크를 그대로 둔다.
          if (!(isEdit && loadedSnapshot?.companyId === companyId)) {
            resolvedCompanyId = companyId;
          }
        } else if (companyAxisUntouched) {
          // 사업자 축 미변경 — companyInfo·companyId 모두 생략해 기존 링크 유지(NTS 미호출).
          // ⛔ 「사업자번호 없음」(NOT_APPLICABLE) 현장도 여기서 걸러야 한다 — 종전엔 아래 noRegistrationNumber 분기가 먼저라
          // 미변경 저장마다 companyInfo{verified:false}를 보냈고, 서버는 그 입력에 **새 Company를 매번** 만들어 수검업체를
          // 바꿨다(소유 ≠ 수검 · prod 31/35곳이 NA · 2026-11-01). 「사업자번호 없음」을 켜고 끄면 `resetCompanyVerification`이
          // `existingCompanyId`를 비워 이 판정이 거짓이 되므로 토글은 아래로 흐른다.
        } else if (noRegistrationNumber) {
          companyInfo = {
            verified: false,
            businessName: companyName.trim() || undefined,
            ceoName: ceoName.trim() || undefined,
            openingDate: openingDate.trim() || undefined,
          };
        } else if (
          isEdit &&
          existingCompanyId &&
          existingStatus === 'FULLY_VERIFIED' &&
          !infoVerified
        ) {
          resolvedCompanyId = existingCompanyId;
        } else if (statusVerified) {
          const normalizedReg = normalizeRegistrationNumber(registrationNumber);
          const has4Fields =
            companyName.trim().length > 0 &&
            ceoName.trim().length > 0 &&
            openingDate.trim().length > 0 &&
            !skipValidateForMismatch;

          companyInfo = {
            verified: true,
            registrationNumber: normalizedReg,
            businessName: companyName.trim() || undefined,
            ceoName: has4Fields ? ceoName.trim() : undefined,
            openingDate: has4Fields ? openingDate.trim() : undefined,
          };
        } else {
          showToast('사업자등록번호를 검증해주세요.', 'error');
          setIsLoading(false);
          return null;
        }

        let savedSite: Site;
        if (isEdit && siteId) {
          const updateData: UpdateSiteDto = {
            name: name.trim(),
            address: address.trim() || undefined,
            addressDetail: addressDetail.trim() || undefined,
            zonecode: zonecode.trim() || undefined,
            latitude: latitude ? parseFloat(latitude) : undefined,
            longitude: longitude ? parseFloat(longitude) : undefined,
            timezone: timezone || undefined,
            // 레거시 자유 텍스트 업종(industryType·industrySubType)은 싣지 않는다 — 읽기 전용(2026-10-30 대체)
            // 바뀐 때만 싣는다(위 ref 주석). 지움은 null이어야 한다(undefined는 「그대로」 — 서버 UpdateSiteDto 주석)
            industryId: industryId !== initialIndustryIdRef.current ? industryId || null : undefined,
            hazardCategories:
              hazardCategories.length > 0 ? hazardCategories : undefined,
            status: isStatusEditable ? status : undefined,
            // 숫자로 보낸다 — 서버 `@IsNumber`라 문자열은 400이었다(web `CreateSiteDto.constructionCost` JSDoc)
            constructionCost: constructionCost.trim() ? Number(constructionCost) : undefined,
            constructionStart: constructionStart || undefined,
            constructionEnd: constructionEnd || undefined,
            managementNumber: managementNumber.trim() || undefined,
            licenseNumber: licenseNumber.trim() || undefined,
            progressRate: progressRate !== '' ? Number(progressRate) : undefined,
            clientName: clientName.trim() || undefined,
            clientContact: clientContact.trim() || undefined,
            // 그룹 이동은 Owner 전용이라 변경분만 실어 보낸다 (비Owner echo가 서버 403을 맞지 않게)
            groupId: canSelectGroup && isGroupChanged ? groupId : undefined,
            companyId: resolvedCompanyId,
            companyInfo,
          };
          savedSite = await sitesApi.update(companyId, siteId, updateData);
        } else {
          const createData: CreateSiteDto = {
            name: name.trim(),
            address: address.trim() || undefined,
            addressDetail: addressDetail.trim() || undefined,
            zonecode: zonecode.trim() || undefined,
            latitude: latitude ? parseFloat(latitude) : undefined,
            longitude: longitude ? parseFloat(longitude) : undefined,
            timezone: timezone || undefined,
            industryId: industryId || undefined,
            hazardCategories:
              hazardCategories.length > 0 ? hazardCategories : undefined,
            // 숫자로 보낸다 — 위 수정 갈래와 같은 이유
            constructionCost: constructionCost.trim() ? Number(constructionCost) : undefined,
            constructionStart: constructionStart || undefined,
            constructionEnd: constructionEnd || undefined,
            managementNumber: managementNumber.trim() || undefined,
            licenseNumber: licenseNumber.trim() || undefined,
            progressRate: progressRate !== '' ? Number(progressRate) : undefined,
            clientName: clientName.trim() || undefined,
            clientContact: clientContact.trim() || undefined,
            // 드롭다운이 노출된 Owner의 선택만 전달 (미노출이면 서버 기본 = 본점)
            groupId: canSelectGroup && groupId ? groupId : undefined,
            companyId: resolvedCompanyId ?? undefined,
            companyInfo,
          };
          // P4 플래그 분기 — 남의 회사를 지정한 생성이 «업체 승인 대기»가 되면 현장 객체가 아니라 요청 응답이 온다
          // (계약 `CreateSiteRequestedResponse`). 현장이 없으니 onSaved(현장 상세 이동·sub-entity 저장)로 흘리지 않는다.
          const created: Site | CreateSiteRequestedResponse = await sitesApi.create(companyId, createData);
          if (isCreateSiteRequested(created)) {
            showToast('업체 소유자에게 현장 등록 요청을 보냈습니다. 승인되면 현장이 만들어집니다.', 'info');
            onRequested?.(created);
            return null;
          }
          savedSite = created;
        }

        if (skipValidateForMismatch) {
          showToast(
            isEdit ? '현장이 저장되었습니다.' : '현장이 추가되었습니다.',
            'success',
          );
        } else if (infoVerified) {
          showToast(
            isEdit
              ? '현장 정보가 저장되었습니다. 사업자 정보 검증 완료.'
              : '현장이 추가되었습니다. 사업자 정보 검증 완료.',
            'success',
          );
        } else {
          showToast(
            isEdit ? '현장 정보가 저장되었습니다.' : '현장이 추가되었습니다.',
            'success',
          );
        }

        onSaved?.(savedSite);
        return savedSite;
      } catch (err) {
        const apiError = extractApiError(err);
        if (apiError.code === 'COMPANY_INFO_MISMATCH') {
          setShowMismatchModal(true);
        } else {
          showToast(resolveErrorMessage(apiError, apiError.message), 'error');
        }
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [
      companyId,
      name,
      noRegistrationNumber,
      sameAsOrganization,
      isEdit,
      existingCompanyId,
      existingStatus,
      infoVerified,
      statusVerified,
      registrationNumber,
      companyName,
      ceoName,
      openingDate,
      siteId,
      address,
      addressDetail,
      zonecode,
      latitude,
      longitude,
      timezone,
      industryId,
      hazardCategories,
      status,
      isStatusEditable,
      groupId,
      isGroupChanged,
      canSelectGroup,
      constructionCost,
      constructionStart,
      constructionEnd,
      managementNumber,
      licenseNumber,
      progressRate,
      clientName,
      clientContact,
      showToast,
      onSaved,
      onRequested,
    ],
  );

  const handleGroupChangeConfirm = useCallback(async (): Promise<Site | null> => {
    setShowGroupChangeConfirm(false);
    groupChangeConfirmedRef.current = true;
    return performSave();
  }, [performSave]);

  const handleGroupChangeCancel = useCallback(() => {
    setShowGroupChangeConfirm(false);
  }, []);

  const handleMismatchRetryVerification = useCallback(() => {
    setShowMismatchModal(false);
    setShowInfoForm(true);
    setInfoVerified(false);
  }, []);

  const handleMismatchSaveWithoutVerification =
    useCallback(async (): Promise<Site | null> => {
      setShowMismatchModal(false);
      setOpeningDate('');
      return performSave(true);
    }, [performSave]);

  // 리터럴로 한 곳에서 구성 — 키 순서 고정(JSON 직렬화 비교) + 입력 전수의 단일 목록.
  // 문자열 값은 저장 payload가 보내는 형태(trim)로 정규화 — 공백만 덧붙인 no-op 저장이
  // dirty 게이트를 활성화하지 않도록 저장 변환과 스냅샷 변환을 일치시킨다.
  const formSnapshot: SiteFormSnapshot = {
    registrationNumber,
    noRegistrationNumber,
    sameAsOrganization,
    companyName: companyName.trim(),
    ceoName: ceoName.trim(),
    openingDate: openingDate.trim(),
    name: name.trim(),
    address: address.trim(),
    addressDetail: addressDetail.trim(),
    zonecode: zonecode.trim(),
    latitude,
    longitude,
    timezone,
    industryId,
    hazardCategories,
    status,
    groupId,
    constructionCost: constructionCost.trim(),
    constructionStart,
    constructionEnd,
    managementNumber: managementNumber.trim(),
    licenseNumber: licenseNumber.trim(),
    progressRate,
    clientName: clientName.trim(),
    clientContact: clientContact.trim(),
  };

  // 건설 단계 판정 — 고른 KSIC가 있으면 그것(대분류 F) · 없으면 레거시 「건설업」(`lib/siteIndustry` — 서버 짝과 같은 규칙)
  const legacyIndustrySource = { industryType: legacyIndustryType, industrySubType: legacyIndustrySubType };
  const isConstructionIndustry = isConstructionSiteIndustry({
    ...legacyIndustrySource,
    industry: industryId && industryCode ? { code: industryCode, name: '' } : null,
  });
  const legacyIndustryLabel = siteIndustryLabel(legacyIndustrySource);
  const companyReady =
    sameAsOrganization || noRegistrationNumber ? true : statusVerified;
  const badgeName = companyName.trim();
  // 저장 가능 조건: Company 해소 + 현장명 + 주소(우편번호) 필수.
  // addressDetail(상세주소)은 선택 입력 (일부 사업장은 우편번호만 존재).
  // O10 — 정보 관리권이 업체에 있으면 저장하지 않는다(서버가 403 `SITE_INFO_MANAGED_BY_OWNER`로 막을 요청이다).
  const canSubmit =
    !siteInfoLocked && companyReady && name.trim().length > 0 && address.trim().length > 0;

  return {
    registrationNumber,
    setRegistrationNumber,
    companyName,
    setCompanyName,
    ceoName,
    setCeoName,
    openingDate,
    setOpeningDate,
    noRegistrationNumber,
    setNoRegistrationNumber,
    sameAsOrganization,
    handleToggleSameAsOrganization,
    organizationCategory,

    isVerifyingStatus,
    statusVerified,
    registrationNumberError,
    existingCompanyId,
    lookupMatchKind,

    showInfoForm,
    setShowInfoForm,
    isVerifyingInfo,
    infoVerified,
    setInfoVerified,
    infoVerifyError,
    setInfoVerifyError,
    ntsTemporaryError,

    existingStatus,

    showMismatchModal,
    setShowMismatchModal,

    name,
    setName,
    address,
    addressDetail,
    zonecode,
    setAddress,
    latitude,
    setLatitude,
    longitude,
    setLongitude,
    timezone,
    setTimezone,
    industryId,
    setIndustryId,
    legacyIndustryLabel,
    hazardCategories,
    toggleHazardCategory,
    status,
    setStatus,

    groupId,
    setGroupId,
    groupOptions,
    canSelectGroup,
    agencyOperatorGroupName: isAgencyOperated ? operatorGroupLabel(operatorGroupId, groupOptions) : null,
    siteInfoLockedReason: siteInfoLocked ? siteInfoLockedReason() : null,
    isGroupChanged,
    showGroupChangeConfirm,
    handleGroupChangeConfirm,
    handleGroupChangeCancel,

    constructionCost,
    setConstructionCost,
    constructionStart,
    setConstructionStart,
    constructionEnd,
    setConstructionEnd,
    managementNumber,
    setManagementNumber,
    licenseNumber,
    setLicenseNumber,
    progressRate,
    setProgressRate,
    clientName,
    setClientName,
    clientContact,
    setClientContact,
    isConstructionOpen,
    setIsConstructionOpen,
    isConstructionIndustry,

    isLoading,
    isFetching,

    companyReady,
    canSubmit,
    badgeName,

    handleVerifyRegistrationNumber,
    handleVerifyInfo,
    handleFillSiteNameFromCompany,
    handleToggleNoRegistration,
    handleRegistrationNumberChange,

    performSave,
    handleMismatchRetryVerification,
    handleMismatchSaveWithoutVerification,

    loadSite,
    resetForm,

    formSnapshot,
  };
}
